import { useMemo } from 'react';
import { Typography, Wrapper } from '@screentone/core';

import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { StructureSlot } from 'data/generated/graphql';
import styles from './LayoutHistory.module.scss';

interface HistoryModuleItemProps {
  module: StructureSlot;
}

export const HistoryModuleItem = ({ module }: HistoryModuleItemProps) => {
  const { treatmentTypeSettings } = useStructureEditContext();

  const currentTreatmentType = module.ttKey;

  const currentTreatmentSetting = treatmentTypeSettings.find(
    (t) => t.treatmentType === currentTreatmentType || t.treatmentTypeKey === currentTreatmentType
  );

  const treatmentLabel = useMemo(() => {
    if (currentTreatmentSetting?.label) return currentTreatmentSetting.label;
    return currentTreatmentType;
  }, [currentTreatmentType, currentTreatmentSetting]);

  const effectiveMin = module.minItemsOverride ?? currentTreatmentSetting?.minRequiredContentItems;
  const effectiveMax = module.maxItemsOverride ?? currentTreatmentSetting?.maxRequiredContentItems;
  const resolvedMin = effectiveMin ?? 1;
  const resolvedMax = effectiveMax ?? '?';
  const itemCountLabel =
    resolvedMin === resolvedMax ? `${resolvedMin} items` : `${resolvedMin} to ${resolvedMax} items`;
  const effectiveMessage =
    effectiveMin != null || effectiveMax != null
      ? `${currentTreatmentSetting?.optional ? 'Optional' : 'Must be present'}. ${itemCountLabel}.`
      : currentTreatmentSetting?.message;

  const uiFieldEntries = useMemo(() => {
    if (!module.uiModuleFields) return [];
    return Object.values(module.uiModuleFields as Record<string, unknown>).flatMap((innerObj) => {
      if (typeof innerObj !== 'object' || innerObj == null) return [];
      return Object.entries(innerObj as Record<string, unknown>)
        .filter(([k, v]) => k !== 'treatmentType' && v != null && typeof v !== 'object')
        .map(([k, v]) => ({ key: k, value: String(v) }));
    });
  }, [module.uiModuleFields]);

  const moduleItems = Array.isArray(module.moduleItems) ? (module.moduleItems as Record<string, unknown>[]) : [];
  const hasContent = uiFieldEntries.length > 0 || moduleItems.length > 0;

  return (
    <Wrapper margin={{ bottom: 'mlg' }}>
      <Typography variant="note" color="asphalt" margin={{ bottom: 'xs' }}>
        {treatmentLabel}
        {currentTreatmentType ? ` (${currentTreatmentType})` : ''}
      </Typography>

      <Wrapper padding={{ all: 'xs' }} className={styles.moduleBox}>
        {uiFieldEntries.map(({ key, value }) => (
          <Typography key={key} variant="note" color="asphalt">
            {key}: {value}
          </Typography>
        ))}
        {moduleItems.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Typography key={i} variant="note" color="asphalt">
            {String(item.hosted_url ?? item.id ?? item.type ?? '—')}
          </Typography>
        ))}
        {!hasContent && (
          <Typography variant="note" color="asphalt">
            Empty slot
          </Typography>
        )}
      </Wrapper>

      {effectiveMessage && (
        <Typography variant="note" color="asphalt" margin={{ top: 'xs', bottom: 'none' }}>
          {effectiveMessage}
        </Typography>
      )}
    </Wrapper>
  );
};
